import {
  ChevronLeft,
  ClipboardList,
  Link2,
  PackagePlus,
  Search,
  Send,
  ShoppingCart,
  X,
  Check,
  Ban,
  Pencil,
  Trash2,
  Eye,
  Info,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { toast } from "sonner";

import styles from "./SolicitacaoCompraPage.module.css";
import { Produto, SolicitacaoCompra } from "../../types/types";
import Popover from "../../components/Popover"; // ajuste o caminho se necessário

const PROJETO_OPTIONS = [
  "MONITORA",
  "CANAL DE INSPEÇÃO",
  "INSPECIONA",
  "TODOS",
];

const EMPTY_SOLICITACAO = {
  nomeProduto: "",
  especificacoes: "",
  quantidadeSolicitada: 1,
  fornecedor: "",
  projeto: "",
  localizacao: "",
  justificativa: "",
  produtoVinculadoId: null as number | null,
};

const EMPTY_NOVO_PRODUTO = {
  nome: "",
  quantidadeMinima: 0,
  quantidadeMaxima: 0,
  fornecedor: "",
  especificacoes: "",
  projeto: "",
  serveParaRaioX: "",
  localizacao: "",
};

const STATUS_BADGE: Record<string, string> = {
  PENDENTE: styles.statusPouco,
  PARCIALMENTE_APROVADO: styles.statusParcial,
  APROVADO: styles.statusEm,
  REJEITADO: styles.statusSemEstoque,
};

const STATUS_LABEL: Record<string, string> = {
  PENDENTE: "PENDENTE",
  PARCIALMENTE_APROVADO: "PARCIAL",
  APROVADO: "APROVADO",
  REJEITADO: "REJEITADO",
};

const SolicitacaoCompraPage = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoCompra[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [statusFiltro, setStatusFiltro] = useState<string>("PENDENTE");

  const [modalNovaShown, setModalNovaShown] = useState(false);
  const [modalDetalhesShown, setModalDetalhesShown] = useState(false);
  const [solicitacaoDetalhe, setSolicitacaoDetalhe] =
    useState<SolicitacaoCompra | null>(null);
  const [modalEditarShown, setModalEditarShown] = useState(false);
  const [solicitacaoEditandoId, setSolicitacaoEditandoId] = useState<
    number | null
  >(null);
  const [formNova, setFormNova] = useState({ ...EMPTY_SOLICITACAO });
  const [buscaProdutoSemelhante, setBuscaProdutoSemelhante] = useState("");
  const [produtoSelecionadoNova, setProdutoSelecionadoNova] =
    useState<Produto | null>(null);

  const [modalAprovarShown, setModalAprovarShown] = useState(false);
  const [solicitacaoAtual, setSolicitacaoAtual] =
    useState<SolicitacaoCompra | null>(null);
  const [modoAprovacao, setModoAprovacao] = useState<"vincular" | "novo">(
    "vincular",
  );
  const [buscaVinculo, setBuscaVinculo] = useState("");
  const [produtoVinculo, setProdutoVinculo] = useState<Produto | null>(null);
  const [novoProdutoForm, setNovoProdutoForm] = useState({
    ...EMPTY_NOVO_PRODUTO,
  });
  const [observacaoAprovacao, setObservacaoAprovacao] = useState("");
  const [quantidadeAprovar, setQuantidadeAprovar] = useState<number>(0);

  const loadSolicitacoes = useCallback(
    async (status?: string) => {
      const query = status ? `?status=${status}` : "";
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        setSolicitacoes(await res.json());
      }
    },
    [token],
  );

  const loadProdutos = useCallback(async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API}/api/Estoque/GetAllProdutos`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      setProdutos(await res.json());
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }
    document.title = "Íkone | Solicitações de Compra";
    loadSolicitacoes(statusFiltro);
    loadProdutos();
    return () => {};
  }, [dispatch, navigate, sessionExpiration, token, loadProdutos]);

  useEffect(() => {
    loadSolicitacoes(statusFiltro);
  }, [statusFiltro, loadSolicitacoes]);

  // ---------- Helpers de quantidade ----------
  const getRestante = (item: SolicitacaoCompra) =>
    (item.quantidadeSolicitada || 0) - (item.quantidadeAprovada || 0);

  const podeAvaliar = (item: SolicitacaoCompra) =>
    item.status === "PENDENTE" || item.status === "PARCIALMENTE_APROVADO";

  const sugestoesNovoModo =
    modoAprovacao === "novo" && novoProdutoForm.nome.trim().length > 1
      ? produtos
          .filter((p) =>
            p.nome
              .toLowerCase()
              .includes(novoProdutoForm.nome.trim().toLowerCase()),
          )
          .slice(0, 6)
      : [];

  // Match exato (mesmo nome, case-insensitive)
  const matchExatoNovo = sugestoesNovoModo.find(
    (p) =>
      p.nome.trim().toLowerCase() === novoProdutoForm.nome.trim().toLowerCase(),
  );

  // ---------- Nova solicitação ----------

  const sugestoesNova =
    buscaProdutoSemelhante.length > 1
      ? produtos
          .filter((p) =>
            p.nome.toLowerCase().includes(buscaProdutoSemelhante.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const handleChangeNova = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormNova((prev) => ({ ...prev, [name]: value }));

    if (name === "nomeProduto") {
      setBuscaProdutoSemelhante(value);
      setProdutoSelecionadoNova(null);
      setFormNova((prev) => ({ ...prev, produtoVinculadoId: null }));
    }
  };

  const selecionarProdutoNova = (produto: Produto) => {
    setProdutoSelecionadoNova(produto);
    setFormNova((prev) => ({
      ...prev,
      nomeProduto: produto.nome,
      produtoVinculadoId: produto.id,
    }));
    setBuscaProdutoSemelhante("");
  };

  const abrirDetalhes = (item: SolicitacaoCompra) => {
    setSolicitacaoDetalhe(item);
    setModalDetalhesShown(true);
  };

  const submitSolicitacao = async () => {
    if (!formNova.nomeProduto.trim()) {
      toast.error("Informe o nome do item.");
      return;
    }
    if (formNova.quantidadeSolicitada <= 0) {
      toast.error("A quantidade deve ser maior que zero.");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...formNova,
            solicitanteUsuario: user?.login || "",
          }),
        },
      );

      if (res.ok) {
        toast.success("Solicitação enviada com sucesso.");
        setFormNova({ ...EMPTY_SOLICITACAO });
        setProdutoSelecionadoNova(null);
        setBuscaProdutoSemelhante("");
        setModalNovaShown(false);
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao enviar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar solicitação.");
    }
  };

  const abrirEdicao = (item: SolicitacaoCompra) => {
    setSolicitacaoEditandoId(item.id);
    setFormNova({
      nomeProduto: item.nomeProduto,
      especificacoes: item.especificacoes || "",
      quantidadeSolicitada: item.quantidadeSolicitada,
      fornecedor: item.fornecedor || "",
      projeto: item.projeto || "",
      localizacao: item.localizacao || "",
      justificativa: item.justificativa || "",
      produtoVinculadoId: item.produtoVinculadoId ?? null,
    });
    setProdutoSelecionadoNova(
      item.produtoVinculadoId
        ? produtos.find((p) => p.id === item.produtoVinculadoId) || null
        : null,
    );
    setBuscaProdutoSemelhante("");
    setModalEditarShown(true);
  };

  const submitEdicao = async () => {
    if (!solicitacaoEditandoId) return;

    if (!formNova.nomeProduto.trim()) {
      toast.error("Informe o nome do item.");
      return;
    }
    if (formNova.quantidadeSolicitada <= 0) {
      toast.error("A quantidade deve ser maior que zero.");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacaoEditandoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formNova),
        },
      );

      if (res.ok) {
        toast.success("Solicitação atualizada com sucesso.");
        setModalEditarShown(false);
        setSolicitacaoEditandoId(null);
        setFormNova({ ...EMPTY_SOLICITACAO });
        setProdutoSelecionadoNova(null);
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao editar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao editar solicitação.");
    }
  };

  const fecharModalNova = () => {
    setModalNovaShown(false);
    setFormNova({ ...EMPTY_SOLICITACAO });
    setProdutoSelecionadoNova(null);
    setBuscaProdutoSemelhante("");
  };

  const fecharModalEditar = () => {
    setModalEditarShown(false);
    setSolicitacaoEditandoId(null);
    setFormNova({ ...EMPTY_SOLICITACAO });
    setProdutoSelecionadoNova(null);
    setBuscaProdutoSemelhante("");
  };

  const excluirSolicitacao = async (solicitacao: SolicitacaoCompra) => {
    if (
      !window.confirm(
        "Deseja excluir essa solicitação? Essa ação não pode ser desfeita.",
      )
    )
      return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacao.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.ok) {
        toast.success("Solicitação excluída.");
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao excluir solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir solicitação.");
    }
  };

  // ---------- Aprovação ----------

  const abrirAprovacao = (solicitacao: SolicitacaoCompra) => {
    setSolicitacaoAtual(solicitacao);
    setObservacaoAprovacao("");
    setBuscaVinculo("");

    const restante = getRestante(solicitacao);
    setQuantidadeAprovar(
      restante > 0 ? restante : solicitacao.quantidadeSolicitada,
    );

    // Pré-preenche formulário de novo produto
    setNovoProdutoForm({
      ...EMPTY_NOVO_PRODUTO,
      nome: solicitacao.nomeProduto,
      especificacoes: solicitacao.especificacoes || "",
      fornecedor: solicitacao.fornecedor || "",
      projeto: solicitacao.projeto || "",
      localizacao: solicitacao.localizacao || "",
    });

    // Tenta encontrar produto já vinculado ou com mesmo nome
    const produtoJaVinculado = solicitacao.produtoVinculadoId
      ? produtos.find((p) => p.id === solicitacao.produtoVinculadoId) || null
      : null;

    const matchPorNome = !produtoJaVinculado
      ? produtos.find(
          (p) =>
            p.nome.trim().toLowerCase() ===
            solicitacao.nomeProduto.trim().toLowerCase(),
        ) || null
      : null;

    if (produtoJaVinculado || matchPorNome) {
      setModoAprovacao("vincular");
      setProdutoVinculo(produtoJaVinculado || matchPorNome);
    } else {
      setModoAprovacao("novo");
      setProdutoVinculo(null);
    }

    setModalAprovarShown(true);
  };

  const sugestoesVinculo =
    buscaVinculo.length > 1
      ? produtos
          .filter((p) =>
            p.nome.toLowerCase().includes(buscaVinculo.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const handleChangeNovoProduto = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setNovoProdutoForm((prev) => ({ ...prev, [name]: value }));
  };

  const confirmarAprovacao = async () => {
    if (!solicitacaoAtual) return;

    const restante = getRestante(solicitacaoAtual);

    if (quantidadeAprovar <= 0 || quantidadeAprovar > restante) {
      toast.error(`Informe uma quantidade entre 1 e ${restante}.`);
      return;
    }

    if (modoAprovacao === "vincular" && !produtoVinculo) {
      toast.error("Selecione o produto existente para vincular.");
      return;
    }
    if (modoAprovacao === "novo" && !novoProdutoForm.nome.trim()) {
      toast.error("Informe o nome do novo produto.");
      return;
    }

    const payload = {
      aprovadorUsuario: user?.login || "",
      observacaoAprovacao: observacaoAprovacao,
      criarNovoProduto: modoAprovacao === "novo",
      produtoVinculadoId:
        modoAprovacao === "vincular" ? produtoVinculo?.id : null,
      novoProduto: modoAprovacao === "novo" ? novoProdutoForm : null,
      quantidadeAprovar: quantidadeAprovar,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacaoAtual.id}/aprovar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        const msg =
          quantidadeAprovar < restante
            ? `Aprovadas ${quantidadeAprovar} unidades. Ainda restam ${
                restante - quantidadeAprovar
              }.`
            : "Solicitação totalmente aprovada e estoque atualizado.";
        toast.success(msg);
        setModalAprovarShown(false);
        setSolicitacaoAtual(null);
        loadSolicitacoes(statusFiltro);
        loadProdutos();
      } else {
        toast.error("Erro ao aprovar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao aprovar solicitação.");
    }
  };

  const rejeitarSolicitacao = async (solicitacao: SolicitacaoCompra) => {
    const restante = getRestante(solicitacao);
    const jaTemAprovado = (solicitacao.quantidadeAprovada || 0) > 0;

    let apenasRestante = false;
    let confirmMsg = "Deseja rejeitar essa solicitação?";

    if (jaTemAprovado && restante > 0) {
      const escolha = window.confirm(
        `Esta solicitação já tem ${solicitacao.quantidadeAprovada} unidade(s) aprovada(s).\n\n` +
          `OK = rejeitar apenas o restante (${restante})\n` +
          `Cancelar = cancelar a operação`,
      );
      if (!escolha) return;
      apenasRestante = true;
      confirmMsg = "";
    } else {
      if (!window.confirm(confirmMsg)) return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacao.id}/rejeitar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            aprovadorUsuario: user?.login || "",
            observacao: "",
            apenasRestante,
          }),
        },
      );

      if (res.ok) {
        toast.success(
          apenasRestante
            ? "Restante rejeitado. Quantidade já aprovada permanece no estoque."
            : "Solicitação rejeitada.",
        );
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao rejeitar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao rejeitar solicitação.");
    }
  };

  // ---------- Render ----------

  const renderLinhaSolicitacao = (item: SolicitacaoCompra) => {
    const dataFormatada = new Date(item.dataSolicitacao).toLocaleString(
      "pt-BR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    );

    const aprovada = item.quantidadeAprovada || 0;
    const restante = getRestante(item);

    return (
      <tr key={item.id}>
        <td
          data-label="Item"
          className={styles.cellName}
          title={item.nomeProduto}
        >
          {item.nomeProduto}
        </td>
        <td data-label="Qtd">
          <div className={styles.qtdCell}>
            <span>{item.quantidadeSolicitada}</span>
            {aprovada > 0 && (
              <span className={styles.qtdMeta}>
                aprovado: {aprovada}
                {restante > 0 ? ` · resta: ${restante}` : ""}
              </span>
            )}
          </div>
        </td>
        <td data-label="Fornecedor">{item.fornecedor || "—"}</td>
        <td data-label="Solicitante">{item.solicitanteUsuario}</td>
        <td data-label="Data">{dataFormatada}</td>
        <td data-label="Status">
          <span
            className={`${styles.badge} ${
              STATUS_BADGE[item.status] || styles.statusPouco
            }`}
          >
            <span className={styles.badgeDot} />
            {STATUS_LABEL[item.status] || item.status}
          </span>
        </td>
        <td data-label="Ações">
          <div className={styles.rowActions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => abrirDetalhes(item)}
              aria-label="Ver detalhes"
            >
              <Eye size={16} />
            </button>

            {podeAvaliar(item) && (
              <>
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                  onClick={() => abrirAprovacao(item)}
                  aria-label="Avaliar solicitação"
                  title="Aprovar (total ou parcial)"
                >
                  <Check size={16} />
                </button>

                {item.solicitanteUsuario === user?.login &&
                  item.status === "PENDENTE" &&
                  aprovada === 0 && (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => abrirEdicao(item)}
                      aria-label="Editar solicitação"
                    >
                      <Pencil size={16} />
                    </button>
                  )}

                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                  onClick={() => rejeitarSolicitacao(item)}
                  aria-label="Rejeitar solicitação"
                  title={
                    aprovada > 0 ? "Rejeitar restante" : "Rejeitar solicitação"
                  }
                >
                  <Ban size={16} />
                </button>

                {item.solicitanteUsuario === user?.login &&
                  item.status === "PENDENTE" &&
                  aprovada === 0 && (
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                      onClick={() => excluirSolicitacao(item)}
                      aria-label="Excluir solicitação"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className={styles.title}>Solicitações de Compra</h1>
        </div>
        <p className={styles.subtitle}>
          Peça a compra de materiais e acompanhe a aprovação. É possível aprovar
          parcialmente a quantidade solicitada.{" "}
          <Popover
            placement="bottom"
            maxWidth={340}
            content={
              <div>
                <p>
                  <strong>Como funciona a aprovação parcial</strong>
                </p>
                <ul>
                  <li>
                    Você pode aprovar só uma parte da quantidade solicitada.
                  </li>
                  <li>
                    A quantidade aprovada <strong>já entra no estoque</strong>{" "}
                    imediatamente.
                  </li>
                  <li>
                    O restante continua pendente e pode ser aprovado depois.
                  </li>
                  <li>
                    Se já existir produto vinculado de uma aprovação anterior,
                    ele é reutilizado automaticamente.
                  </li>
                  <li>
                    Quando o restante chegar a zero, o status vira{" "}
                    <strong>APROVADO</strong>.
                  </li>
                </ul>
              </div>
            }
          >
            <Info
              size={15}
              style={{
                verticalAlign: "-2px",
                marginLeft: 4,
                opacity: 0.65,
                cursor: "help",
              }}
            />
          </Popover>
        </p>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          <div className={styles.spacer} />
          <div className={styles.actionsGroup}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setModalNovaShown(true)}
            >
              <ShoppingCart size={16} />
              Nova solicitação
            </button>
          </div>
        </div>

        <div className={styles.filters}>
          <select
            className={styles.select}
            value={statusFiltro}
            onChange={(ev) => setStatusFiltro(ev.target.value)}
          >
            <option value="PENDENTE">Pendentes</option>
            <option value="PARCIALMENTE_APROVADO">
              Parcialmente aprovadas
            </option>
            <option value="APROVADO">Aprovadas</option>
            <option value="REJEITADO">Rejeitadas</option>
            <option value="">Todas</option>
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Qtd</th>
              <th>Fornecedor</th>
              <th>Solicitante</th>
              <th>Data</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {solicitacoes.length > 0 ? (
              solicitacoes.map(renderLinhaSolicitacao)
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className={styles.emptyState}>
                    <ClipboardList size={32} />
                    <p>Nenhuma solicitação encontrada.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ========== MODAL DETALHES ========== */}
      <Modal
        show={modalDetalhesShown}
        onHide={() => setModalDetalhesShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <Eye size={20} />
              Detalhes da solicitação
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {solicitacaoDetalhe && (
            <div className={styles.form}>
              <p className={styles.approvalSummary}>
                <strong>{solicitacaoDetalhe.nomeProduto}</strong> —{" "}
                <span
                  className={`${styles.badge} ${
                    STATUS_BADGE[solicitacaoDetalhe.status] ||
                    styles.statusPouco
                  }`}
                >
                  <span className={styles.badgeDot} />
                  {STATUS_LABEL[solicitacaoDetalhe.status] ||
                    solicitacaoDetalhe.status}
                </span>
              </p>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Quantidade solicitada</label>
                  <p>{solicitacaoDetalhe.quantidadeSolicitada}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Quantidade aprovada</label>
                  <p>{solicitacaoDetalhe.quantidadeAprovada || 0}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Restante a comprar</label>
                  <p className={styles.restanteHighlight}>
                    {getRestante(solicitacaoDetalhe)}
                  </p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Fornecedor sugerido</label>
                  <p>{solicitacaoDetalhe.fornecedor || "—"}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Projeto</label>
                  <p>{solicitacaoDetalhe.projeto || "—"}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Localização</label>
                  <p>{solicitacaoDetalhe.localizacao || "—"}</p>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Especificações</label>
                <p>{solicitacaoDetalhe.especificacoes || "—"}</p>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Justificativa</label>
                <p>{solicitacaoDetalhe.justificativa || "—"}</p>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Solicitado por</label>
                  <p>{solicitacaoDetalhe.solicitanteUsuario}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Data da solicitação</label>
                  <p>
                    {new Date(
                      solicitacaoDetalhe.dataSolicitacao,
                    ).toLocaleString("pt-BR")}
                  </p>
                </div>
              </div>

              {solicitacaoDetalhe.status !== "PENDENTE" && (
                <>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label className={styles.label}>
                        {solicitacaoDetalhe.status === "REJEITADO"
                          ? "Rejeitado por"
                          : "Último aprovador"}
                      </label>
                      <p>{solicitacaoDetalhe.aprovadorUsuario || "—"}</p>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>
                        {solicitacaoDetalhe.status === "REJEITADO"
                          ? "Data da rejeição"
                          : "Última atualização"}
                      </label>
                      <p>
                        {solicitacaoDetalhe.dataAprovacao
                          ? new Date(
                              solicitacaoDetalhe.dataAprovacao,
                            ).toLocaleString("pt-BR")
                          : "—"}
                      </p>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>
                      Histórico / Observações
                    </label>
                    <pre className={styles.obsHistorico}>
                      {solicitacaoDetalhe.observacaoAprovacao || "—"}
                    </pre>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalDetalhesShown(false)}
          >
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== MODAL EDITAR ========== */}
      <Modal
        show={modalEditarShown}
        onHide={fecharModalEditar}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <Pencil size={20} />
              Editar solicitação de compra
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.form}>
            <div className={styles.field} style={{ position: "relative" }}>
              <label htmlFor="nomeProdutoEdit" className={styles.label}>
                Nome do item
              </label>
              <input
                type="text"
                id="nomeProdutoEdit"
                name="nomeProduto"
                className={styles.input}
                value={formNova.nomeProduto}
                onChange={handleChangeNova}
                autoComplete="off"
              />
              {produtoSelecionadoNova && (
                <p className={styles.fileChosen}>
                  <Link2
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "0.3rem" }}
                  />
                  Vinculado a item já cadastrado: {produtoSelecionadoNova.nome}
                </p>
              )}
              {sugestoesNova.length > 0 && !produtoSelecionadoNova && (
                <div className={styles.historyBox}>
                  {sugestoesNova.map((p) => (
                    <div
                      key={p.id}
                      className={styles.historyItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => selecionarProdutoNova(p)}
                    >
                      <span className={styles.historyIcon}>
                        <Search size={14} />
                      </span>
                      <div className={styles.historyBody}>
                        <span className={styles.historyType}>{p.nome}</span>
                        <span className={styles.historyMeta}>
                          Estoque atual: {p.quantidade} — {p.localizacao}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label
                  htmlFor="quantidadeSolicitadaEdit"
                  className={styles.label}
                >
                  Quantidade
                </label>
                <input
                  type="number"
                  id="quantidadeSolicitadaEdit"
                  name="quantidadeSolicitada"
                  className={styles.input}
                  value={formNova.quantidadeSolicitada}
                  onChange={handleChangeNova}
                  min={1}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="fornecedorEdit" className={styles.label}>
                  Fornecedor sugerido
                </label>
                <input
                  type="text"
                  id="fornecedorEdit"
                  name="fornecedor"
                  className={styles.input}
                  value={formNova.fornecedor}
                  onChange={handleChangeNova}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="projetoEdit" className={styles.label}>
                  Projeto
                </label>
                <select
                  id="projetoEdit"
                  name="projeto"
                  className={styles.selectField}
                  value={formNova.projeto}
                  onChange={handleChangeNova}
                >
                  <option value="">Selecione o projeto</option>
                  {PROJETO_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="localizacaoEdit" className={styles.label}>
                  Localização
                </label>
                <input
                  type="text"
                  id="localizacaoEdit"
                  name="localizacao"
                  className={styles.input}
                  value={formNova.localizacao}
                  onChange={handleChangeNova}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="especificacoesEdit" className={styles.label}>
                Especificações
              </label>
              <textarea
                id="especificacoesEdit"
                name="especificacoes"
                className={styles.textarea}
                value={formNova.especificacoes}
                onChange={handleChangeNova}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="justificativaEdit" className={styles.label}>
                Justificativa
              </label>
              <textarea
                id="justificativaEdit"
                name="justificativa"
                className={styles.textarea}
                value={formNova.justificativa}
                onChange={handleChangeNova}
                placeholder="Por que esse material é necessário?"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={fecharModalEditar}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={submitEdicao}
          >
            <Send size={16} />
            Salvar alterações
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== MODAL NOVA SOLICITAÇÃO ========== */}
      <Modal
        show={modalNovaShown}
        onHide={fecharModalNova}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <ShoppingCart size={20} />
              Solicitar compra de material
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.form}>
            <div className={styles.field} style={{ position: "relative" }}>
              <label htmlFor="nomeProduto" className={styles.label}>
                Nome do item
              </label>
              <input
                type="text"
                id="nomeProduto"
                name="nomeProduto"
                className={styles.input}
                value={formNova.nomeProduto}
                onChange={handleChangeNova}
                autoComplete="off"
              />
              {produtoSelecionadoNova && (
                <p className={styles.fileChosen}>
                  <Link2
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "0.3rem" }}
                  />
                  Vinculado a item já cadastrado: {produtoSelecionadoNova.nome}
                </p>
              )}
              {sugestoesNova.length > 0 && !produtoSelecionadoNova && (
                <div className={styles.historyBox}>
                  {sugestoesNova.map((p) => (
                    <div
                      key={p.id}
                      className={styles.historyItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => selecionarProdutoNova(p)}
                    >
                      <span className={styles.historyIcon}>
                        <Search size={14} />
                      </span>
                      <div className={styles.historyBody}>
                        <span className={styles.historyType}>{p.nome}</span>
                        <span className={styles.historyMeta}>
                          Estoque atual: {p.quantidade} — {p.localizacao}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="quantidadeSolicitada" className={styles.label}>
                  Quantidade
                </label>
                <input
                  type="number"
                  id="quantidadeSolicitada"
                  name="quantidadeSolicitada"
                  className={styles.input}
                  value={formNova.quantidadeSolicitada}
                  onChange={handleChangeNova}
                  min={1}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="fornecedor" className={styles.label}>
                  Fornecedor sugerido
                </label>
                <input
                  type="text"
                  id="fornecedor"
                  name="fornecedor"
                  className={styles.input}
                  value={formNova.fornecedor}
                  onChange={handleChangeNova}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="projeto" className={styles.label}>
                  Projeto
                </label>
                <select
                  id="projeto"
                  name="projeto"
                  className={styles.selectField}
                  value={formNova.projeto}
                  onChange={handleChangeNova}
                >
                  <option value="">Selecione o projeto</option>
                  {PROJETO_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="localizacao" className={styles.label}>
                  Localização
                </label>
                <input
                  type="text"
                  id="localizacao"
                  name="localizacao"
                  className={styles.input}
                  value={formNova.localizacao}
                  onChange={handleChangeNova}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="especificacoes" className={styles.label}>
                Especificações
              </label>
              <textarea
                id="especificacoes"
                name="especificacoes"
                className={styles.textarea}
                value={formNova.especificacoes}
                onChange={handleChangeNova}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="justificativa" className={styles.label}>
                Justificativa
              </label>
              <textarea
                id="justificativa"
                name="justificativa"
                className={styles.textarea}
                value={formNova.justificativa}
                onChange={handleChangeNova}
                placeholder="Por que esse material é necessário?"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={fecharModalNova}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={submitSolicitacao}
          >
            <Send size={16} />
            Enviar solicitação
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== MODAL APROVAR (com quantidade parcial) ========== */}
      <Modal
        show={modalAprovarShown}
        onHide={() => setModalAprovarShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <Check size={20} />
              Avaliar solicitação — {solicitacaoAtual?.nomeProduto}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {solicitacaoAtual && (
            <div className={styles.form}>
              <p className={styles.approvalSummary}>
                Solicitado por{" "}
                <strong>{solicitacaoAtual.solicitanteUsuario}</strong>
                {solicitacaoAtual.justificativa
                  ? ` — "${solicitacaoAtual.justificativa}"`
                  : ""}
              </p>

              {/* Resumo de quantidades */}
              <div className={styles.qtdResumoBox}>
                <div className={styles.qtdResumoItem}>
                  <span className={styles.qtdResumoLabel}>Solicitado</span>
                  <span className={styles.qtdResumoValue}>
                    {solicitacaoAtual.quantidadeSolicitada}
                  </span>
                </div>
                <div className={styles.qtdResumoItem}>
                  <span className={styles.qtdResumoLabel}>Já aprovado</span>
                  <span className={styles.qtdResumoValue}>
                    {solicitacaoAtual.quantidadeAprovada || 0}
                  </span>
                </div>
                <div className={styles.qtdResumoItem}>
                  <span className={styles.qtdResumoLabel}>
                    Restante{" "}
                    <Popover
                      placement="top"
                      maxWidth={260}
                      content={
                        <p>
                          Quantidade que ainda falta ser aprovada. Enquanto
                          houver restante, o status permanece{" "}
                          <strong>PARCIAL</strong>.
                        </p>
                      }
                    >
                      <Info
                        size={12}
                        style={{
                          opacity: 0.6,
                          cursor: "help",
                          verticalAlign: "-1px",
                        }}
                      />
                    </Popover>
                  </span>
                  <span
                    className={`${styles.qtdResumoValue} ${styles.restanteHighlight}`}
                  >
                    {getRestante(solicitacaoAtual)}
                  </span>
                </div>
              </div>

              {/* Quantidade a aprovar agora */}
              <div className={styles.field}>
                <label htmlFor="quantidadeAprovar" className={styles.label}>
                  Quantidade a aprovar agora{" "}
                  <Popover
                    placement="top"
                    maxWidth={300}
                    content={
                      <div>
                        <p>
                          <strong>Aprovação parcial</strong>
                        </p>
                        <ul>
                          <li>
                            Informe quantas unidades deseja aprovar{" "}
                            <em>agora</em>.
                          </li>
                          <li>
                            Essa quantidade{" "}
                            <strong>já é adicionada ao estoque</strong>.
                          </li>
                          <li>
                            O que sobrar continua como pendente e pode ser
                            aprovado em outra hora.
                          </li>
                        </ul>
                      </div>
                    }
                  >
                    <Info
                      size={14}
                      style={{
                        marginLeft: 4,
                        opacity: 0.7,
                        cursor: "help",
                        verticalAlign: "-2px",
                      }}
                    />
                  </Popover>
                </label>
                <input
                  type="number"
                  id="quantidadeAprovar"
                  className={styles.input}
                  min={1}
                  max={getRestante(solicitacaoAtual)}
                  value={quantidadeAprovar}
                  onChange={(e) => {
                    const restante = getRestante(solicitacaoAtual!);
                    let val = Number(e.target.value) || 0;
                    if (val > restante) val = restante;
                    if (val < 0) val = 0;
                    setQuantidadeAprovar(val);
                  }}
                />
                <p className={styles.hintText}>
                  Você pode aprovar parcialmente. O restante continua pendente.
                </p>
              </div>

              {/* Toggle vincular / novo */}
              <div
                className={styles.actionsGroup}
                style={{ marginBottom: "1rem" }}
              >
                <button
                  type="button"
                  className={`${styles.btn} ${
                    modoAprovacao === "vincular"
                      ? styles.btnGhostActive
                      : styles.btnGhost
                  }`}
                  onClick={() => setModoAprovacao("vincular")}
                >
                  <Link2 size={16} />
                  Vincular a item existente
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${
                    modoAprovacao === "novo"
                      ? styles.btnGhostActive
                      : styles.btnGhost
                  }`}
                  onClick={() => setModoAprovacao("novo")}
                >
                  <PackagePlus size={16} />
                  Cadastrar como novo item
                </button>
              </div>

              {modoAprovacao === "vincular" ? (
                <div className={styles.field} style={{ position: "relative" }}>
                  <label className={styles.label}>Buscar item no estoque</label>
                  <div className={styles.searchWrap}>
                    <Search size={16} />
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Digite o nome do item..."
                      value={
                        produtoVinculo ? produtoVinculo.nome : buscaVinculo
                      }
                      onChange={(e) => {
                        setBuscaVinculo(e.target.value);
                        setProdutoVinculo(null);
                      }}
                    />
                    {produtoVinculo && (
                      <button
                        type="button"
                        className={styles.searchClearBtn}
                        onClick={() => {
                          setProdutoVinculo(null);
                          setBuscaVinculo("");
                        }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {sugestoesVinculo.length > 0 && !produtoVinculo && (
                    <div className={styles.historyBox}>
                      {sugestoesVinculo.map((p) => (
                        <div
                          key={p.id}
                          className={styles.historyItem}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setProdutoVinculo(p);
                            setBuscaVinculo("");
                          }}
                        >
                          <div className={styles.historyBody}>
                            <span className={styles.historyType}>{p.nome}</span>
                            <span className={styles.historyMeta}>
                              Estoque atual: {p.quantidade} — {p.localizacao}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {produtoVinculo && (
                    <div className={styles.previewBox}>
                      Após esta aprovação o estoque ficará em{" "}
                      <strong>
                        {produtoVinculo.quantidade + quantidadeAprovar}
                      </strong>{" "}
                      (atual {produtoVinculo.quantidade} + {quantidadeAprovar})
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Aviso de match exato */}
                  {matchExatoNovo && (
                    <div
                      className={styles.previewBox}
                      style={{
                        borderColor: "#f59e0b",
                        background: "#fffbeb",
                        marginBottom: "1rem",
                      }}
                    >
                      <strong>Já existe um item com este nome:</strong>
                      <br />
                      <span>
                        {matchExatoNovo.nome} — Estoque atual:{" "}
                        {matchExatoNovo.quantidade} (
                        {matchExatoNovo.localizacao || "sem localização"})
                      </span>
                      <div style={{ marginTop: "0.6rem" }}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnGhostActive}`}
                          onClick={() => {
                            setModoAprovacao("vincular");
                            setProdutoVinculo(matchExatoNovo);
                            setBuscaVinculo("");
                          }}
                        >
                          <Link2 size={16} />
                          Agregar quantidade a este item
                        </button>
                      </div>
                    </div>
                  )}

                  <div className={styles.field}>
                    <label htmlFor="novoNome" className={styles.label}>
                      Nome do item
                    </label>
                    <input
                      type="text"
                      id="novoNome"
                      name="nome"
                      className={styles.input}
                      value={novoProdutoForm.nome}
                      onChange={handleChangeNovoProduto}
                      autoComplete="off"
                    />
                  </div>

                  {/* Sugestões enquanto digita no modo novo */}
                  {sugestoesNovoModo.length > 0 && !matchExatoNovo && (
                    <div
                      className={styles.historyBox}
                      style={{ marginBottom: "1rem" }}
                    >
                      <p
                        style={{
                          fontSize: "0.8rem",
                          color: "#64748b",
                          margin: "0 0 0.4rem 0",
                          padding: "0 0.5rem",
                        }}
                      >
                        Itens existentes parecidos — clique para agregar em vez
                        de criar novo:
                      </p>
                      {sugestoesNovoModo.map((p) => (
                        <div
                          key={p.id}
                          className={styles.historyItem}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setModoAprovacao("vincular");
                            setProdutoVinculo(p);
                            setBuscaVinculo("");
                          }}
                        >
                          <span className={styles.historyIcon}>
                            <Link2 size={14} />
                          </span>
                          <div className={styles.historyBody}>
                            <span className={styles.historyType}>{p.nome}</span>
                            <span className={styles.historyMeta}>
                              Estoque atual: {p.quantidade} —{" "}
                              {p.localizacao || "—"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label htmlFor="novoQtdMin" className={styles.label}>
                        Quantidade mínima
                      </label>
                      <input
                        type="number"
                        id="novoQtdMin"
                        name="quantidadeMinima"
                        className={styles.input}
                        value={novoProdutoForm.quantidadeMinima}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoQtdMax" className={styles.label}>
                        Quantidade máxima
                      </label>
                      <input
                        type="number"
                        id="novoQtdMax"
                        name="quantidadeMaxima"
                        className={styles.input}
                        value={novoProdutoForm.quantidadeMaxima}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoFornecedor" className={styles.label}>
                        Fornecedor
                      </label>
                      <input
                        type="text"
                        id="novoFornecedor"
                        name="fornecedor"
                        className={styles.input}
                        value={novoProdutoForm.fornecedor}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoLocalizacao" className={styles.label}>
                        Localização
                      </label>
                      <input
                        type="text"
                        id="novoLocalizacao"
                        name="localizacao"
                        className={styles.input}
                        value={novoProdutoForm.localizacao}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label
                      htmlFor="novoEspecificacoes"
                      className={styles.label}
                    >
                      Especificações
                    </label>
                    <textarea
                      id="novoEspecificacoes"
                      name="especificacoes"
                      className={styles.textarea}
                      value={novoProdutoForm.especificacoes}
                      onChange={handleChangeNovoProduto}
                    />
                  </div>

                  <div className={styles.previewBox}>
                    {matchExatoNovo ? (
                      <>
                        Ao confirmar, a quantidade será{" "}
                        <strong>agregada</strong> ao item existente (o backend
                        também evita duplicata).
                      </>
                    ) : (
                      <>
                        Será cadastrado um <strong>novo item</strong> com{" "}
                        <strong>{quantidadeAprovar}</strong> unidade(s) nesta
                        aprovação.
                        {solicitacaoAtual.produtoVinculadoId && (
                          <span>
                            {" "}
                            (produto já existe de aprovação anterior)
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              <div className={styles.field}>
                <label htmlFor="observacaoAprovacao" className={styles.label}>
                  Observações da aprovação
                </label>
                <textarea
                  id="observacaoAprovacao"
                  className={styles.textarea}
                  value={observacaoAprovacao}
                  onChange={(e) => setObservacaoAprovacao(e.target.value)}
                  placeholder="Opcional"
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalAprovarShown(false)}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnSuccess}`}
            onClick={confirmarAprovacao}
          >
            <Send size={16} />
            Confirmar aprovação
            {quantidadeAprovar > 0 ? ` (${quantidadeAprovar})` : ""}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SolicitacaoCompraPage;
